import React, { useEffect, useState, type ReactNode } from "react";
import { useStore, useSelector } from "react-redux";
import { addGeneralLedgerAccount } from "src/apiService/modules/generalLedgerAccounts";
import { addBankAccount } from "src/apiService/modules/bank-accounts";
import { bankAccountsSelector } from "src/store/bankAccount/selector";
import { filteredSalesSelector } from "src/store/sale/selector";
import { filteredExpensesSelector } from "src/store/transaction/selector";

import { integrationsSelector } from "src/store/plaidIntegration/selector";
import { toDateString } from "src/utils/date";
import type { BankAccount } from "src/interfaces/bank-account.interface";
import { getNameFromIntegration } from "./AddBankAccount";

export interface InitialPopulationProps {
  children: ReactNode;
  fallback: ReactNode;
}
let Running: undefined | Promise<void> = undefined;

function InitialPopulation({ children, fallback }: InitialPopulationProps) {
  const store = useStore();
  const bankAccounts = useSelector(bankAccountsSelector);
  const [loading, setLoading] = useState(
    !!Running || bankAccounts.length === 0
  );
  useEffect(() => {
    if (Running) {
      Running.then(() => setLoading(false));
      return;
    }

    Running = (async () => {
      try {
        setLoading(true);
        const state = store.getState();
        const bankAccounts = bankAccountsSelector(state);
        const integrations = [...integrationsSelector(state)];
        if (bankAccounts.length <= 0) {
          const date = new Date();
          date.setMonth(date.getMonth() - 3);
          const sales = filteredSalesSelector(state, [
            {
              items: [
                {
                  columnField: "sale_date",
                  operatorValue: "onOrAfter",
                  value: toDateString(date),
                },
              ],
            },
          ]);

          const platforms = [
            ...new Set(sales.map((s) => s.sale_platform).filter(Boolean)),
          ] as string[];

          const knownPlatformNames = new Set<string>(platforms);

          for (const p of platforms) {
            const glAccount = await addGeneralLedgerAccount(p, [
              "assets",
              "bank-account-cash",
            ]);

            const bankAccount: Omit<BankAccount, "id" | "user"> = {
              general_ledger_account_id: glAccount.id,
              name: p,
              type: "sale-platform",
            };

            const integrationIndex = integrations.findIndex(
              (i: any) =>
                i.name === p ||
                i.username === p ||
                i.account_name === p ||
                (p === "Poshmark" && i.type === "poshmark") ||
                getNameFromIntegration(i) === p
            );

            if (integrationIndex > -1) {
              const integration = integrations[integrationIndex] as any;
              bankAccount.integration_id = integration.id;
              if (integration.name) knownPlatformNames.add(integration.name);
              integrations.splice(integrationIndex, 1);
            }

            await addBankAccount(bankAccount);
          }

          const transactions = filteredExpensesSelector(state, [
            {
              items: [
                {
                  columnField: "date",
                  operatorValue: "onOrAfter",
                  value: toDateString(date),
                },
                {
                  columnField: "type",
                  operatorValue: "notEquals",
                  value: "sale",
                },
              ],
            },
          ]);

          const transactionsBankAccounts = [
            ...new Set(
              transactions
                .map((s) => s.integration_name)
                .filter((name) => name && !knownPlatformNames.has(name))
            ),
          ] as string[];
          for (const p of transactionsBankAccounts) {
            const glAccount = await addGeneralLedgerAccount(p, [
              "assets",
              "bank-account-cash",
            ]);

            const integrationIndex = integrations.findIndex(
              (i: any) =>
                i.name === p ||
                i.username === p ||
                i.account_name === p ||
                (p === "Poshmark" && i.type === "poshmark")
            );

            const matchedIntegration =
              integrationIndex > -1
                ? integrations[integrationIndex]
                : undefined;

            const bankAccount: Omit<BankAccount, "id" | "user"> = {
              general_ledger_account_id: glAccount.id,
              name: p,
              type:
                matchedIntegration && matchedIntegration.type !== "expense"
                  ? "sale-platform"
                  : "bank-account",
            };

            if (matchedIntegration) {
              bankAccount.integration_id = matchedIntegration.id;
              integrations.splice(integrationIndex, 1);
            }

            await addBankAccount(bankAccount);
          }
        }

        // Check if all integrations have a bank account /  sale platform
        for (const integration of integrations) {
          if (bankAccounts.find((ba) => ba.integration_id === integration.id))
            continue;

          const name = getNameFromIntegration(integration);
          const glAccount = await addGeneralLedgerAccount(name, [
            "assets",
            "bank-account-cash",
          ]);
          const bankAccount: Omit<BankAccount, "id" | "user"> = {
            name,
            integration_id: integration.id,
            general_ledger_account_id: glAccount.id,
            type:
              integration.type === "expense" ? "bank-account" : "sale-platform",
          };

          await addBankAccount(bankAccount);
        }
      } finally {
        Running = undefined;
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <>{loading ? fallback : children}</>;
}

export default InitialPopulation;
